import { useState } from "react";
import { api, getUser, setUser } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Building2, Phone, CheckCircle2, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";

// Carrier Profile Edit — small self-contained card that lets an existing
// carrier update the company name + contact phone shown on their bids.
// Both fields became mandatory at signup in Feb 2026, but older carriers
// signed up without them; this card is the painless backfill path so ops
// don't have to touch the DB.
export default function CarrierProfileCard({ onChanged }) {
  const user = getUser();
  const [company, setCompany] = useState(user?.company || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);

  if (!user || user.role !== "carrier") return null;

  const missing = !user.company || !user.phone;
  const dirty = (company || "").trim() !== (user.company || "") || (phone || "").trim() !== (user.phone || "");

  const save = async () => {
    const c = (company || "").trim();
    const p = (phone || "").trim();
    if (!c) { toast.error("Company or trading name is required"); return; }
    if (!p) { toast.error("Contact phone number is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/carriers/${user.id}/profile`,
        { company: c, phone: p },
        { params: { user_id: user.id } },
      );
      // Refresh the local user object so the Dashboard chrome and every
      // downstream card sees the new values without a hard reload.
      setUser({ ...user, company: data.company, phone: data.phone });
      toast.success("Profile updated");
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      data-testid="carrier-profile-card"
      className="rounded-2xl border-[#E2DFD8] p-6 bg-white"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#1B3624] text-white flex items-center justify-center">
              <Building2 className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#6B6965] font-semibold">Business profile</div>
              <h2 className="text-xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
                Company &amp; phone
              </h2>
            </div>
          </div>
          <p className="mt-2 text-sm text-[#6B6965] max-w-lg">
            Shown to customers on your bids and used by Binshare ops to reach you on the day of a collection.
          </p>
        </div>
        {missing ? (
          <div
            data-testid="carrier-profile-status-missing"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#B84E2A] font-semibold bg-[#D67B54]/10 border border-[#D67B54]/40 rounded-full px-3 py-1"
          >
            <AlertTriangle className="w-3 h-3" /> Backfill needed
          </div>
        ) : (
          <div
            data-testid="carrier-profile-status-complete"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#4A6B56] font-semibold bg-[#4A6B56]/10 border border-[#4A6B56]/25 rounded-full px-3 py-1"
          >
            <CheckCircle2 className="w-3 h-3" /> Complete
          </div>
        )}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="carrier-profile-company" className="text-sm">Company or trading name</Label>
          <Input
            id="carrier-profile-company"
            data-testid="carrier-profile-company-input"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="e.g. Greenwheels Waste Ltd"
            className="rounded-xl mt-1"
          />
        </div>
        <div>
          <Label htmlFor="carrier-profile-phone" className="text-sm">Contact phone number</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B6965]" />
            <Input
              id="carrier-profile-phone"
              data-testid="carrier-profile-phone-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 07123 456789"
              className="rounded-xl mt-1 pl-8"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end">
        <Button
          data-testid="carrier-profile-save-btn"
          onClick={save}
          disabled={saving || !dirty}
          className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white min-h-[44px] px-6 disabled:opacity-50"
        >
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </Card>
  );
}
